import { useState } from 'react';
import { Alert, Box, Button, Card, Typography } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { startPublicIntake } from '../../../services/publicIntakeService';

/**
 * The GENERAL intake portal — a standing URL (/intake) the lab hands out the
 * way the Excel workbook used to circulate. "Start a test request" mints a
 * fresh draft and drops the client into their own tokenized form; the token
 * URL is theirs to bookmark and return to.
 */
export const PublicIntakeStartPage = () => {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const { token } = await startPublicIntake();
      navigate(`/intake/${token}`, { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Could not start a request — please try again.');
      setStarting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2, bgcolor: '#f5f5f5' }}>
      <Card sx={{ maxWidth: 620, width: '100%', borderRadius: '12px' }} elevation={3}>
        <Box sx={{ bgcolor: '#3E4A3E', color: '#fff', py: 3, px: 4, borderRadius: '12px 12px 0 0' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>BGC Engineering</Typography>
          <Typography variant="subtitle2" sx={{ opacity: 0.85, mt: 0.5 }}>
            Geotechnical Laboratory — Test Request Portal
          </Typography>
        </Box>
        <Box sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Request laboratory testing in three steps: your project information, the samples
            and tests you need, and a final review.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your request saves automatically as you go — after starting, bookmark the page's
            address to come back and finish later. When you submit, the lab reviews your
            request and will be in touch.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Button
            variant="contained" size="large" startIcon={<PlayArrow />}
            onClick={start} disabled={starting}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {starting ? 'Starting…' : 'Start a test request'}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default PublicIntakeStartPage;
